import { useLocation, useNavigate, useNavigation } from "react-router";
import type { ActionFunctionArgs } from "react-router";
import { useSuspenseInfiniteQuery } from "@tanstack/react-query";
import { MessageSquare } from "lucide-react";
import { api } from "@/lib/api";
import { KINDS, STATUSES, filtersOf, searchOf, type BoardFilters } from "@/lib/feedback";
import { requireSession } from "@/lib/guards";
import { queries } from "@/lib/queries";
import { readPages } from "@/lib/query";
import { errorMessage } from "@/lib/session";
import { PageTitle } from "@/components/brand";
import { CreateButton } from "@/components/create-button";
import { EmptyNote } from "@/components/empty-note";
import { FeedbackCard } from "@/components/feedback-card";
import { ToggleChips } from "@/components/form";
import { BackLink, TopBar } from "@/components/navigation";
import { ShowMore, rowsOf } from "@/components/show-more";

/**
 * The feedback board (0091, which turned the page that only emailed into a
 * shared one): what every tester sent, newest first, 30 at a time, with the
 * runner's status and reply on each. Chips filter by kind and by status; they
 * live in the address, so Back and a reload keep them, and an address naming
 * none shows both kinds and what is still open. Reached from Account, and
 * only where a deployment takes feedback — anywhere else every address under
 * `/feedback` is a 404 like any other.
 *
 * "Me too" posts here by fetcher, from the cards and from a feedback's own
 * page.
 */

/** The session, for a page that exists only where a deployment takes feedback. */
export async function feedbackSession(request: Request) {
  const session = await requireSession(request);
  if (!session.feedback) throw new Response("Not Found", { status: 404 });
  return session;
}

export async function feedbackLoader({ request }: { request: Request }) {
  await feedbackSession(request);
  await readPages(queries.feedback(filtersOf(new URL(request.url).search)));
  return null;
}

export type BackingOutcome = { error?: string };

export async function feedbackAction({ request }: ActionFunctionArgs): Promise<BackingOutcome> {
  const form = await request.formData();
  const id = String(form.get("id") ?? "");
  const intent = form.get("intent");
  if (!id || (intent !== "back" && intent !== "unback")) throw new Response("Bad Request", { status: 400 });
  try {
    if (intent === "back") await api.feedback.back(id);
    else await api.feedback.unback(id);
  } catch (error) {
    return { error: errorMessage(error, "Could not save your me too.") };
  }
  return {};
}

export default function FeedbackBoard() {
  const navigate = useNavigate();
  const location = useLocation();
  const pending = useNavigation().location;
  // The chips show where a tap is taking the page, ahead of the read it waits on.
  const shown = filtersOf(pending && pending.pathname === location.pathname ? pending.search : location.search);
  const filtered = searchOf(filtersOf(location.search)) !== "";

  const toggle = (group: keyof BoardFilters, value: string, on: boolean) => {
    const next = { ...shown, [group]: on ? [...shown[group], value] : shown[group].filter((held) => held !== value) };
    void navigate({ search: searchOf(next) }, { replace: true });
  };
  const chips = (group: keyof BoardFilters, known: readonly { value: string; label: string }[]) =>
    known.map((option) => ({
      name: `${group}-${option.value}`,
      label: option.label,
      on: shown[group].includes(option.value),
      onChange: (on: boolean) => toggle(group, option.value, on),
    }));

  return (
    <section className="space-y-6">
      <PageTitle of={["Feedback"]} />
      <div className="space-y-2">
        <TopBar>
          <BackLink to="/account">Account</BackLink>
        </TopBar>
        <h1 className="page-title">Feedback</h1>
        <p className="text-muted-foreground">
          What testers have sent, and where it stands. Tap “Me too” on what you'd like fixed or added as well.
        </p>
      </div>

      <div className="space-y-3">
        <ToggleChips label="Kind" options={chips("kind", KINDS)} />
        <ToggleChips label="Status" options={chips("status", STATUSES)} />
      </div>

      <Cards filters={filtersOf(location.search)} filtered={filtered} />
      <CreateButton label="Send feedback" to="/feedback/new" />
    </section>
  );
}

/** What the filters in the address found, a page at a time. The loader read the first. */
function Cards({ filters, filtered }: { filters: BoardFilters; filtered: boolean }) {
  const pages = useSuspenseInfiniteQuery(queries.feedback(filters));
  const rows = rowsOf(pages.data, (page) => page.feedback, (feedback) => feedback.id);
  const now = Date.now();

  if (rows.length === 0) {
    return (
      <EmptyNote icon={MessageSquare}>
        {filtered ? "Nothing matches these filters." : "Nothing open right now. Done and Won't do are behind their chips."}
      </EmptyNote>
    );
  }
  return (
    <>
      <ul className="space-y-4">
        {rows.map((feedback) => (
          <li key={feedback.id}>
            <FeedbackCard feedback={feedback} now={now} />
          </li>
        ))}
      </ul>
      <ShowMore query={pages} />
    </>
  );
}
